import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { FileDown, ShieldCheck, ClipboardCheck, Handshake } from "lucide-react";
import SEOHead from "@/components/site/SEOHead";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inr = (n) => `₹${new Intl.NumberFormat("en-IN").format(Math.round(n || 0))}`;
const GROUPS = [["ready", "Ready — meets the standard", "text-emerald-700 bg-emerald-50 border-emerald-200"], ["minor", "Minor improvement suggested", "text-sky-700 bg-sky-50 border-sky-200"], ["major", "Major improvement needed", "text-amber-700 bg-amber-50 border-amber-200"], ["missing", "Missing — needs to be created", "text-red-700 bg-red-50 border-red-200"]];

function OwnerReport({ token }) {
  const [rep, setRep] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { axios.get(`${API}/public/readiness/report/${token}`).then(({ data }) => setRep(data)).catch(() => setErr(true)); }, [token]);
  if (err) return <div className="container-x py-32 text-center text-graphite">This report link is not valid. Please contact Happy Retreats.</div>;
  if (!rep) return <div className="container-x py-32 text-center text-graphite">Loading your report…</div>;
  const p = rep.profile || {};
  return (
    <div className="container-x py-14" data-testid="owner-readiness-report">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <div className="overline text-clay">Property readiness report · Version {rep.version} · {rep.date}</div>
          <h1 className="font-display text-3xl md:text-4xl font-light mt-3">{p.property_name || "Your property"}</h1>
          <div className="text-sm text-graphite mt-2">{p.property_type || "—"} · {p.property_stage || "—"}{p.rooms ? ` · ${p.rooms} rooms` : ""}</div>
        </div>
        <a href={`${API}/public/readiness/report/${token}/pdf`} className="bg-ink text-ivory px-5 py-3 text-[11px] uppercase tracking-[0.18em] inline-flex items-center gap-2 hover:bg-clay transition-colors" data-testid="owner-report-pdf">
          <FileDown className="w-4 h-4" /> Download PDF report
        </a>
      </div>

      <div className="mt-8 bg-ink text-ivory p-6 md:p-8 flex flex-wrap items-center gap-8" data-testid="owner-score-band">
        <div><div className="text-[10px] uppercase tracking-[0.18em] text-ivory/50">Overall readiness</div><div className="font-display text-5xl mt-1 text-[#C9A876]">{rep.score}%</div></div>
        <div className="flex-1 min-w-[220px]">
          <div className="text-lg font-medium">{rep.classification}</div>
          <div className={`text-sm mt-1 ${rep.blocker_count ? "text-red-300" : "text-emerald-300"}`}>{rep.blocker_count} launch blocker{rep.blocker_count !== 1 ? "s" : ""} {rep.blocker_count ? "to resolve before opening" : "— nothing blocks your launch"}</div>
          {rep.decision && <div className="text-xs mt-2 text-ivory/70">Happy Retreats decision: <strong>{rep.decision}</strong></div>}
        </div>
      </div>

      {rep.blockers.length > 0 && (
        <div className="mt-6 border border-red-200 bg-red-50 p-5">
          <div className="text-sm font-medium text-red-700 mb-2">Must be resolved before launch</div>
          <ul className="text-xs text-red-700 space-y-1">{rep.blockers.map((b, i) => <li key={i}>· {b.name} ({b.category}){b.cost ? ` — est. ${inr(b.cost)}` : ""}</li>)}</ul>
        </div>
      )}

      <div className="mt-8 grid md:grid-cols-2 gap-5">
        {GROUPS.map(([k, label, cls]) => (
          <div key={k} className="bg-white border border-ink/10 p-5" data-testid={`owner-group-${k}`}>
            <div className={`inline-block text-[11px] border px-2 py-1 rounded ${cls} mb-3`}>{label} · {rep.groups[k].length}</div>
            <div className="space-y-2 max-h-72 overflow-y-auto pr-2">
              {rep.groups[k].map((r, i) => (
                <div key={i} className="text-xs border-b border-ink/5 pb-2">
                  <div className="font-medium">{r.name} <span className="text-graphite font-normal">· {r.category}</span></div>
                  {(r.why || r.note) && <div className="text-graphite mt-0.5">{r.note || r.why}</div>}
                  {(k === "major" || k === "missing") && <div className="text-graphite mt-0.5">{r.cost ? `Est. ${inr(r.cost)} · ` : ""}{r.responsibility}{r.deadline ? ` · target ${r.deadline}` : ""}</div>}
                </div>
              ))}
              {rep.groups[k].length === 0 && <div className="text-xs text-graphite/60">Nothing in this list.</div>}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8 bg-white border border-ink/10 p-6" data-testid="owner-investment">
        <div className="overline text-clay mb-4">Estimated readiness investment</div>
        <div className="grid sm:grid-cols-2 gap-x-10 gap-y-2 text-sm">
          <div className="flex justify-between border-b border-ink/5 pb-1.5"><span className="text-graphite">Funded by you (the owner)</span><span className="tabular-nums font-medium">{inr(rep.investment.owner + rep.investment.shared_owner)}</span></div>
          <div className="flex justify-between border-b border-ink/5 pb-1.5"><span className="text-graphite">Funded by Happy Retreats</span><span className="tabular-nums font-medium">{inr(rep.investment.happy_retreats + rep.investment.shared_hr)}</span></div>
          <div className="flex justify-between border-b border-ink/5 pb-1.5"><span className="text-graphite">Handled as operating expense</span><span className="tabular-nums">{inr(rep.investment.operating_expense)}</span></div>
          <div className="flex justify-between border-b border-ink/5 pb-1.5"><span className="text-graphite">Responsibility to be agreed</span><span className="tabular-nums">{inr(rep.investment.tbd + rep.investment.third_party)}</span></div>
          <div className="flex justify-between border-b border-ink/5 pb-1.5"><span className="text-graphite">Needed before launch</span><span className="tabular-nums font-medium">{inr(rep.investment.launch_blocker + rep.investment.mandatory_pre)}</span></div>
          <div className="flex justify-between border-b border-ink/5 pb-1.5 font-semibold"><span>Total readiness investment</span><span className="tabular-nums">{inr(rep.investment.total)}</span></div>
        </div>
      </div>

      <p className="mt-6 text-[11px] text-graphite/70 leading-relaxed max-w-4xl">{rep.disclaimer}</p>
      <div className="mt-8">
        <Link to="/partner-with-us" className="bg-ink text-ivory px-6 py-3.5 text-[11px] uppercase tracking-[0.2em] inline-flex items-center gap-2 hover:bg-clay transition-colors"><Handshake className="w-4 h-4" /> Discuss next steps with Happy Retreats</Link>
      </div>
    </div>
  );
}

export default function PropertyReadiness() {
  const { token } = useParams();
  return (
    <div data-testid="property-readiness-page">
      <SEOHead title="Property Readiness Assessment | Happy Retreats" description="A transparent, structured readiness assessment for your property — what's ready, what's missing, what it costs and who funds it, before we partner." />
      {token ? (
        <div className="pt-36 md:pt-44 bg-bone min-h-screen"><OwnerReport token={token} /></div>
      ) : (
        <>
          <section className="bg-ink text-ivory pt-44 md:pt-52 pb-16">
            <div className="container-x">
              <div className="overline text-ivory/70 animate-fade-up">Before we partner</div>
              <h1 className="display-1 mt-8 max-w-4xl animate-fade-up" style={{ animationDelay: "0.15s" }}>Is your property <span className="italic text-clay">ready</span> to host celebrations?</h1>
              <p className="mt-8 max-w-2xl text-base md:text-lg text-ivory/80 font-light leading-relaxed animate-fade-up" style={{ animationDelay: "0.3s" }}>
                Our structured readiness assessment covers legal approvals, safety, guest rooms, kitchens, event spaces, utilities and more — so you know exactly what's ready, what's missing, what it costs and who funds it, before any agreement is signed.
              </p>
              <div className="mt-10 flex flex-wrap gap-3 animate-fade-up" style={{ animationDelay: "0.4s" }}>
                <Link to="/partner-with-us#enquire" className="bg-ivory text-ink px-6 py-3.5 text-[11px] uppercase tracking-[0.2em] font-medium inline-flex items-center gap-2 hover:bg-clay hover:text-white transition-colors" data-testid="pr-cta-request">
                  <ClipboardCheck className="w-4 h-4" /> Request a readiness assessment
                </Link>
                <Link to="/revenue-calculator" className="border border-ivory/40 px-6 py-3.5 text-[11px] uppercase tracking-[0.2em] font-medium inline-flex items-center gap-2 hover:bg-ivory/10 transition-colors">Estimate your revenue first</Link>
              </div>
            </div>
          </section>
          <section className="bg-bone py-16">
            <div className="container-x grid md:grid-cols-3 gap-6">
              {[["A 500-point inspection", "29 areas — from fire safety and water systems to linen counts — assessed on site by the Happy Retreats team."],
                ["Transparent investment plan", "Every gap gets an estimated cost and a clearly agreed funder: you, Happy Retreats, shared, or operating expense."],
                ["A clear go / no-go answer", "A readiness score, launch blockers and a simple recommendation — nothing hidden in the fine print."]].map(([t, d], i) => (
                <div key={i} className="bg-white border border-ink/10 p-6">
                  <ShieldCheck className="w-5 h-5 text-clay" />
                  <div className="font-display text-lg mt-3">{t}</div>
                  <p className="text-sm text-graphite mt-2 leading-relaxed">{d}</p>
                </div>
              ))}
            </div>
          </section>
        </>
      )}
    </div>
  );
}
